import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '~/common/components/ui/button';
import { LucideArrowLeft, LucideChevronDown, LucideChevronRight, LucideChevronUp, LucideX, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ISearchFilterIdItemDto } from '~/dtos/hub/hub.models.generated';
import { summarize } from '~/common/utils/stringUtil';
import { Popover, PopoverContent, PopoverTrigger } from '~/common/components/ui/popover';
import isEqual from 'lodash/isEqual';
import { LoadingButton } from '~/common/components/LoadingButton';
import { useLocation } from 'react-router';
import { FilterEntriesList } from '~/features/catalog/components/filter/FilterEntriesList';
import { cn } from '~/common/utils/styleUtil';
import { Toggle } from '~/common/components/ui/toggle';
import { formatNumber } from '~/common/utils/numberUtil';
import { useScreenSize } from '~/common/hooks/useScreenSize';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '~/common/components/ui/dialog';
import { useBasePath } from '~/common/hooks/useBasePath.ts';
import { useFilterItemsQuery } from '~/features/catalog/hooks/useFilterItemsQuery.ts';
import { getLabel } from '../../utils/filterNameUtil';
import { useQuery } from '@tanstack/react-query';
import { clientFetch } from '~/common/utils/clientFetch.client';
import { buildCatalogSearchQuery } from '~/features/catalog/utils/catalogSearchKeyUtil.ts';
import { useWidth } from '~/common/hooks/useWidth.ts';
import { isPrintableSearchKey } from '~/features/catalog/utils/filterKeyboardUtil.ts';

export interface SelectableFilterItem<T> {
  item: T;
  selected: boolean;
  isFeatured: boolean;
  label: string;
}

const mapItems = (
  items: ISearchFilterIdItemDto[],
  selectedItems: ISearchFilterIdItemDto[]
): SelectableFilterItem<ISearchFilterIdItemDto>[] => {
  const featured = items.filter(x => x.isFeatured); //.sort(compareFunction);
  const others = items.filter(x => !x.isFeatured); //.sort(compareFunction);
  return [...featured, ...others].map(x => {
    return {
      item: { ...x, labelValue: x.name as string },
      selected: selectedItems.some(s => s.id === x.id),
      isFeatured: x.isFeatured,
      label: x.name
    };
  });
};

function buildFetchUrl(
  baseUrl: string,
  searchParams: URLSearchParams,
  searchKey: string,
  items: SelectableFilterItem<ISearchFilterIdItemDto>[],
  forceIncludeSelection: boolean
) {
  const url = new URLSearchParams(searchParams);
  url.delete('page');

  if (forceIncludeSelection && searchKey) {
    url.set(
      searchKey,
      items
        .filter(x => x.selected)
        .map(x => x.item.id)
        .join(',')
    );
  }

  return `${baseUrl}?${url.toString()}`;
}

function buildFilterUrl(
  baseUrl: string,
  searchParams: URLSearchParams,
  searchKey: string,
  items: SelectableFilterItem<ISearchFilterIdItemDto>[],
  forceIncludeSelection: boolean
) {
  const url = new URLSearchParams(searchParams);

  if (forceIncludeSelection && searchKey) {
    const selectedItems = items.filter(x => x.selected).map(x => x.item.id);

    if (selectedItems?.length > 0) {
      url.set(searchKey, selectedItems.join(','));
    }
  }

  return { path: baseUrl, query: url.toString() };
}

interface MultiSelectionItemFilterProps {
  searchKey: string;
  liveSelectedItems: ISearchFilterIdItemDto[];
  onApplyFilter: (selectedItems: number[]) => void;
  filterTitle: string;
  canSearch?: boolean;
  refreshItemsOnEverySelection?: boolean;
  itemRestUrl?: string;
  staticOptions?: ISearchFilterIdItemDto[];
  onBack?: (selectedItems: number[]) => void;
  area: string;
  disabled?: boolean;
  compactTrigger?: boolean;
}

export function MultiSelectionItemFilter({
  filterTitle,
  liveSelectedItems,
  onApplyFilter,
  itemRestUrl,
  staticOptions,
  refreshItemsOnEverySelection = false,
  canSearch = true,
  searchKey,
  onBack,
  area,
  disabled = false,
  compactTrigger = false
}: Readonly<MultiSelectionItemFilterProps>) {
  const isStatic = !!staticOptions;
  const suppressTotalCount = isStatic;
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<SelectableFilterItem<ISearchFilterIdItemDto>[]>([]);
  const [open, setOpen] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [itemsSearchText, setItemsSearchText] = useState('');
  const itemSearchTextInputRef = useRef<HTMLInputElement | null>(null);
  const location = useLocation();
  const { isMobile, isDesktop } = useScreenSize();
  const { path } = useBasePath();
  const myRef = useRef(null);
  const [ref, width] = useWidth(myRef);

  const fetchUrl = useMemo(
    () =>
      isStatic
        ? ''
        : buildFetchUrl(
            itemRestUrl!,
            new URLSearchParams(location.search),
            searchKey,
            items,
            refreshItemsOnEverySelection
          ),
    [isStatic, itemRestUrl, location.search, searchKey, items, refreshItemsOnEverySelection]
  );

  const { data: fetchedItems } = useFilterItemsQuery(searchKey, area, fetchUrl, open && !isStatic);

  const hasSearchChanged = useMemo(
    () =>
      !isEqual(
        items.filter(x => x.selected).map(x => x.item.name),
        liveSelectedItems
      ),
    [items, liveSelectedItems]
  );

  const totalCountUrl = useMemo(
    () =>
      buildFilterUrl(`${path}/resources/articles/search`, new URLSearchParams(location.search), searchKey, items, true),
    [path, location.search, searchKey, items]
  );

  const noOfActiveFilter = items.filter(x => x.selected).length;
  const hasActiveFilter = noOfActiveFilter > 0;
  const hasActiveLiveFilter = liveSelectedItems.length > 0;

  const searchQuery = useQuery({
    queryKey: buildCatalogSearchQuery(location.pathname, new URLSearchParams(totalCountUrl.query ?? '')),
    queryFn: async () => {
      const res = await clientFetch(`${totalCountUrl.path}?${totalCountUrl.query.toString()}`);
      return await res.json();
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    enabled: open && hasSearchChanged && hasActiveFilter && !suppressTotalCount
  });

  const isTotalCountLoading = suppressTotalCount ? false : searchQuery.isPending;
  const totalCount = suppressTotalCount ? undefined : (searchQuery.data?.searchResponse?.totalCount ?? undefined);

  const handleApplyFilter = useCallback(() => {
    onApplyFilter(items.filter(x => x.selected).map(x => x.item.id));
    setOpen(false);
  }, [items, onApplyFilter]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack(items.filter(x => x.selected).map(x => x.item.id));
    }
    setOpen(false);
  }, [items, onBack]);

  const handleReset = useCallback(() => {
    setItems(prev => prev.map(x => ({ ...x, selected: false })));
  }, []);

  const handleSearchToggle = useCallback(() => {
    setSearchActive(prev => !prev);
  }, []);

  useEffect(() => {
    if (searchActive && itemSearchTextInputRef.current) {
      itemSearchTextInputRef.current.focus();
    }
  }, [searchActive]);

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchActive(false);
      setItemsSearchText('');
    }
  }, [open]);

  const handleAutoSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!canSearch || searchActive) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (!isPrintableSearchKey(event)) return;
      event.preventDefault();
      setSearchActive(true);
      setItemsSearchText(event.key);
    },
    [canSearch, searchActive]
  );

  const handleEscapeKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (searchActive && itemsSearchText.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        setItemsSearchText('');
      }
    },
    [searchActive, itemsSearchText]
  );

  const filterEntriesListId = `filter-entries-${searchKey}`;

  const maxChars = useMemo(() => {
    if (width <= 0) return 26; // Fallback
    const chars = Math.floor(width * 0.11);
    return Math.max(26, Math.min(chars, 60)) - 4;
  }, [width]);

  const filterHeader = useMemo(
    () => (
      <div>
        {isMobile ? (
          <DialogTitle asChild className={'text-left pb-2 flex items-center'}>
            <div>
              {onBack && (
                <Button
                  variant={'ghost'}
                  className={'pl-0'}
                  onClick={handleBack}
                  data-testid={`dropdownfilter-${filterTitle}`}
                >
                  <LucideArrowLeft />
                </Button>
              )}
              {filterTitle}
            </div>
          </DialogTitle>
        ) : (
          <div>
            <div className={'flex items-center flex-row justify-between'}>
              <div className={'font-bold'}>{filterTitle}</div>
              <Button
                variant={'ghost'}
                size={'sm'}
                onClick={handleApplyFilter}
                data-testid={`dropdownfilter-${filterTitle}`}
              >
                <LucideX className={'text-secondary'} />
              </Button>
            </div>
          </div>
        )}
        <div className={'flex flex-row gap-2 ml-[-10px] mb-1'}>
          <Button
            size={'sm'}
            variant={'ghost'}
            disabled={!hasActiveFilter}
            onClick={handleReset}
            data-testid={`dropdownfilter-reset-${filterTitle}`}
          >
            {t('Zurücksetzen')}
          </Button>
          {canSearch && (
            <Toggle
              size={'sm'}
              variant={'outline'}
              onClick={handleSearchToggle}
              data-testid={`dropdownfilter-search-toggle-${filterTitle}`}
            >
              {t('Suchen')}
            </Toggle>
          )}
        </div>
        {searchActive && (
          <div className={cn('w-full flex border border-gray-300 rounded')}>
            <input
              className="w-full p-2 px-2 outline-hidden rounded-md border-none"
              ref={itemSearchTextInputRef}
              value={itemsSearchText}
              placeholder={t('Suche')}
              aria-label={t('Suche')}
              role="combobox"
              aria-expanded={searchActive}
              aria-controls={filterEntriesListId}
              onChange={event => setItemsSearchText(event.target.value)}
              data-testid={`dropdownfilter-search-input-${filterTitle}`}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
            />
            {itemsSearchText?.length > 0 && (
              <button
                type="reset"
                className="mr-2"
                onClick={() => setItemsSearchText('')}
                data-testid={`dropdownfilter-search-clear-${filterTitle}`}
              >
                <XIcon className={'text-secondary'} />
              </button>
            )}
          </div>
        )}
        <span role="status" aria-live="polite" className="sr-only">
          {searchActive ? t('Suchfeld geöffnet') : ''}
        </span>
      </div>
    ),
    [
      canSearch,
      filterTitle,
      filterEntriesListId,
      handleApplyFilter,
      handleBack,
      handleReset,
      handleSearchToggle,
      hasActiveFilter,
      isMobile,
      itemsSearchText,
      onBack,
      searchActive,
      t
    ]
  );

  const filterFooterActions = useMemo(() => {
    if (hasSearchChanged && !suppressTotalCount) {
      return (
        <LoadingButton
          variant={'secondary'}
          className={'w-full'}
          loading={isTotalCountLoading}
          onClick={handleApplyFilter}
        >
          <span>
            {totalCount && totalCount > 0 && <span>{`${formatNumber(totalCount, i18n.language)} `}</span>}
            <span>{t(totalCount === 1 ? 'Produkt anzeigen' : 'Produkte anzeigen')}</span>
          </span>
        </LoadingButton>
      );
    }
    if (hasSearchChanged) {
      return (
        <Button
          className={'w-full'}
          variant={'secondary'}
          onClick={handleApplyFilter}
          data-testid={`dropdownfilter-apply-${filterTitle}`}
        >
          {t('Anwenden')}
        </Button>
      );
    }
    return (
      <Button
        className={'w-full'}
        variant={'secondary'}
        onClick={handleApplyFilter}
        data-testid={`dropdownfilter-close-${filterTitle}`}
      >
        {t('Schliessen')}
      </Button>
    );
  }, [
    hasSearchChanged,
    suppressTotalCount,
    isTotalCountLoading,
    handleApplyFilter,
    totalCount,
    i18n.language,
    t,
    filterTitle
  ]);

  useEffect(() => {
    if (isStatic && staticOptions) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setItems(mapItems(staticOptions, liveSelectedItems));
      return;
    }
    if (fetchedItems) {
      setItems(mapItems(fetchedItems, liveSelectedItems));
    }
  }, [fetchedItems, liveSelectedItems, isStatic, staticOptions]);

  const handleItemSelection = useCallback((key: string | number | boolean) => {
    setItems(prev => {
      return prev.map(x => {
        if (x.item.id === key) {
          return { ...x, selected: !x.selected };
        }
        return x;
      });
    });
  }, []);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && open && hasSearchChanged) {
        onApplyFilter(items.filter(x => x.selected).map(x => x.item.id));
      }
      setOpen(newOpen);
    },
    [open, onApplyFilter, items, hasSearchChanged]
  );

  const handleDirectReset = useCallback(
    e => {
      e.stopPropagation();
      onApplyFilter([]);
    },
    [onApplyFilter]
  );

  const visibleItems = useMemo(
    () => (searchActive ? items.filter(x => x.item.name.toLowerCase().includes(itemsSearchText.toLowerCase())) : items),
    [items, itemsSearchText, searchActive]
  );

  const filterSelector = useMemo(() => {
    const useDesktopStyle = isDesktop || compactTrigger;
    return (
      <div
        className={cn(
          compactTrigger
            ? 'flex border rounded h-10 px-0'
            : 'flex h-14 border-b px-2 desktop:px-0 desktop:border desktop:rounded desktop:h-10',
          isMobile && !compactTrigger && (hasActiveLiveFilter || open) && 'text-primary',
          useDesktopStyle && (hasActiveLiveFilter || open) && 'text-primary border-primary bg-primary-light'
        )}
        ref={ref}
      >
        <button
          type="button"
          className={cn(
            'px-2 py-2 rounded whitespace-nowrap text-base w-full max-w-full flex items-center justify-between',
            hasActiveLiveFilter && (compactTrigger ? 'bg-primary-light' : 'desktop:bg-primary-light')
          )}
          data-testid={`dropdownfilter-${filterTitle}`}
          onClick={() => {
            handleOpenChange(!open);
          }}
          disabled={disabled}
        >
          <FilterText
            filterTitle={filterTitle}
            liveSelectedItems={liveSelectedItems}
            hasActiveLiveFilter={hasActiveLiveFilter}
            searchKey={searchKey}
            disabled={disabled}
            maxChars={maxChars}
          />

          {useDesktopStyle && !hasActiveLiveFilter && (
            <div>
              {open ? (
                <LucideChevronUp size={14} className={'text-primary'} />
              ) : (
                <LucideChevronDown size={14} className={'text-secondary'} />
              )}
            </div>
          )}
          {isMobile && !compactTrigger && (
            <div className={''}>
              <LucideChevronRight size={14} className={cn(hasActiveLiveFilter && 'text-primary')} />
            </div>
          )}
        </button>
        {useDesktopStyle && hasActiveLiveFilter && (
          <button
            type="button"
            className={'ml-1 px-2 bg-primary-light rounded'}
            onClick={handleDirectReset}
            data-testid={`dropdownfilter-reset-${filterTitle}`}
            disabled={disabled}
          >
            <LucideX size={18} />
          </button>
        )}
      </div>
    );
  }, [
    maxChars,
    filterTitle,
    handleDirectReset,
    handleOpenChange,
    hasActiveLiveFilter,
    isDesktop,
    isMobile,
    liveSelectedItems,
    open,
    disabled,
    ref,
    searchKey,
    compactTrigger
  ]);

  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange} key={fetchUrl}>
        <DialogTrigger asChild>{filterSelector}</DialogTrigger>
        <DialogContent
          showFullscreenOnMobile
          overlayClassName={'slide-out-to-left slide-in-from-left'}
          onKeyDown={handleAutoSearchKeyDown}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          <DialogHeader>{filterHeader}</DialogHeader>
          <div id={filterEntriesListId}>
            <FilterEntriesList
              selectableItems={visibleItems}
              onItemSelectionChanged={handleItemSelection}
              height={searchActive ? 'calc(100dvh - 250px)' : 'calc(100dvh - 210px)'}
            />
          </div>
          <DialogFooter>{filterFooterActions}</DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{filterSelector}</PopoverTrigger>
      <PopoverContent
        className="w-80 h-[360px] flex flex-col justify-between content-between gap-1"
        onKeyDown={handleAutoSearchKeyDown}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <div>{filterHeader}</div>
        <div id={filterEntriesListId}>
          <FilterEntriesList
            height={searchActive ? 145 : 185}
            selectableItems={visibleItems}
            onItemSelectionChanged={handleItemSelection}
          />
        </div>
        <div>{filterFooterActions}</div>
      </PopoverContent>
    </Popover>
  );
}

function FilterText({
  filterTitle,
  liveSelectedItems,
  hasActiveLiveFilter,
  searchKey,
  disabled = false,
  maxChars
}: Readonly<{
  filterTitle: string;
  liveSelectedItems: ISearchFilterIdItemDto[];
  hasActiveLiveFilter: boolean;
  searchKey: string;
  disabled?: boolean;
  maxChars: number;
}>) {
  const { t } = useTranslation();
  const selectedLabels = useMemo(
    () =>
      liveSelectedItems.map(x => {
        return x.name ? x.name : t(getLabel(x.id, searchKey) as any);
      }),
    [liveSelectedItems, searchKey, t]
  );

  const { summarizedText, notVisibleItems } = useMemo(() => {
    return summarize(selectedLabels, maxChars, filterTitle);
  }, [maxChars, filterTitle, selectedLabels]);

  return (
    <div className={'flex w-[calc(100%-2rem)] desktop:w-auto'}>
      <div
        className={cn(
          'hidden desktop:block text-black mr-2',
          disabled && 'text-secondary',
          hasActiveLiveFilter && 'text-primary'
        )}
      >
        {hasActiveLiveFilter ? summarizedText : filterTitle}
      </div>
      <div className={cn('block desktop:hidden text-black mr-2', disabled && 'text-secondary')}>{filterTitle}</div>
      <div className={cn('block desktop:hidden text-primary truncate', disabled && 'text-secondary')}>
        {hasActiveLiveFilter && summarizedText}
      </div>
      {notVisibleItems > 0 && <div>{` +${notVisibleItems}`}</div>}
    </div>
  );
}
